import { useState, useCallback, useEffect } from "react";
import { Validate } from "api/interfaces/commonInterface.interface";
import { type Option } from "utils/options";
import { ValidateMsg } from "api/interfaces/commonInterface.interface";
import {
  SelectGroup,
  VideoGuidePdfDto,
  VideoGuideSetting,
} from "api/interfaces/managerInterface.interface";
import useHelpGuideApi, { HelpGuideGroup } from "hook/useHelpGuideApi";
import { buildGuideItem, notifyByType } from "../../VideoManagerContainer";
import AddPdfPresenter from "./AddPdfPresenter";
import useApiError from "hook/useApiError";
import { checkEmpty } from "utils/functions";

interface Props {
  groups: HelpGuideGroup[];
  groupsIsFetching: boolean;
  isEdit?: boolean; // 편집 모드일 때
  selectedGroup: SelectGroup;
  onModal: (type?: string) => void;
  selectedVideo: VideoGuideSetting | undefined;
  onClickDeactive?: (userId: string) => void;
  maxPosition: number;
  getHelpGuides: () => void;
}

export enum UPDATE_TYPE {
  CATEGORY,
  GROUP,
  TITLE,
  ORDER,
  DESC,
}

function AddPdfContainer(props: Props): JSX.Element {
  const [validate, setValidate] = useState<Validate>({
    title: false,
  });
  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
    title: "",
    file: "",
  });
  const { addHelpGuideItem, updateHelpGuideItem } = useHelpGuideApi();

  const [pdfItemInfo, setPdfItemInfo] = useState<VideoGuidePdfDto>({
    groupId: props.selectedGroup.groupId,
    groupName: props.selectedGroup.groupName,
    title: "",
    thumbnailUrl: "",
    description: "",
    position: 1,
    resourceUrl: "",
    resourceType: "",
    id: "",
  });
  const { intlString, lang } = useApiError();

  const [isInProcess, setIsInProcess] = useState<boolean>(false);

  useEffect(() => {
    if (!checkEmpty(props.selectedGroup.groupId)) {
      setPdfItemInfo((info) => {
        return { ...info, groupId: props.selectedGroup.groupId as string };
      });
    }
  }, [props.selectedGroup]);

  useEffect(() => {
    if (props.isEdit && props.selectedVideo !== undefined) {
      setPdfItemInfo((info) => {
        return {
          ...info,
          ...props.selectedVideo,
          groupId: props.selectedGroup.groupId,
          groupName: props.selectedGroup.groupName,
        };
      });
    }
  }, [props.isEdit, props.selectedGroup.groupId, props.selectedVideo]);

  const onChangeUserInfo = useCallback(
    (type: UPDATE_TYPE, value: string | number | string[]) => {
      if (type === UPDATE_TYPE.TITLE) {
        setPdfItemInfo((info) => {
          return { ...info, title: value as string };
        });
      } else if (type === UPDATE_TYPE.ORDER) {
        setPdfItemInfo((info) => {
          return { ...info, position: value as number };
        });
      } else if (type === UPDATE_TYPE.DESC) {
        setPdfItemInfo((info) => {
          return { ...info, description: value as string };
        });
      } else if (type === UPDATE_TYPE.GROUP) {
        setPdfItemInfo((info) => {
          const groupFound = groupOptions.find(
            (group) => group.value === value
          );
          return {
            ...info,
            groupId: groupFound?.value as string,
            groupName: groupFound?.label as string,
          };
        });
      }
    },
    []
  );

  const onSaveBefore = useCallback(async () => {
    setValidate((info) => {
      return { ...info, title: false };
    });
    setValidateMsg((info) => {
      return { ...info, title: "" };
    });

    let checkedFlag = false;
    if (checkEmpty(pdfItemInfo.title)) {
      checkedFlag = true;
      setValidate((info) => {
        return { ...info, title: true };
      });
      setValidateMsg((info) => {
        return {
          ...info,
          title: intlString(lang.label.subscription.sendEmail.title.empty),
        };
      });
    }
    if (!pdfItemInfo.file) {
      checkedFlag = true;
      setValidate((info) => {
        return { ...info, file: true };
      });
      setValidateMsg((info) => {
        return {
          ...info,
          file: intlString(lang.validateMsg.manager.video.file),
        };
      });
    }

    if (checkedFlag) {
      return;
    }
    setIsInProcess(true);
    if (!pdfItemInfo.file && !pdfItemInfo.id) return Promise.resolve();

    const item = buildGuideItem(pdfItemInfo, "pdf", pdfItemInfo.file);
    const isUpdate = !!pdfItemInfo.id;
    const action = isUpdate ? updateHelpGuideItem : addHelpGuideItem;

    return action(item, pdfItemInfo.file)
      .then(() => {
        notifyByType(
          "success",
          item.resourceType,
          isUpdate ? "update" : "create"
        );
        props.getHelpGuides();
      })
      .catch(() =>
        notifyByType("error", item.resourceType, isUpdate ? "update" : "create")
      )
      .finally(() => {
        setIsInProcess(false);
        props.onModal();
      });
  }, [props, pdfItemInfo]);

  const handleFileChange = (e: any) => {
    let file = e.target.files[0];
    if (!file) {
      return;
    }
    setPdfItemInfo({
      ...pdfItemInfo,
      file: file,
      fileName: file.name,
    });
  };

  const groupOptions = props.groups
    .filter((group: HelpGuideGroup) => group.layout === "table")
    .map((group: HelpGuideGroup) => {
      return {
        value: group.groupId as string,
        label: group.groupName,
        order: group.sortOrder,
      };
    });

  return (
    <AddPdfPresenter
      isEdit={props.isEdit ?? false}
      onModal={props.onModal}
      pdfItemInfo={pdfItemInfo}
      validate={validate}
      validateMsg={validateMsg}
      isInProcess={isInProcess}
      onChangeUserInfo={onChangeUserInfo}
      onSaveBefore={onSaveBefore}
      maxPosition={props.maxPosition}
      groupOptions={groupOptions}
      groupOptionsIsFetching={props.groupsIsFetching}
      handleFileChange={handleFileChange}
    />
  );
}

export default AddPdfContainer;
